// src/components/workspace/IncidentReportForm.js
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { X, ShieldCheck, Plus, AlertTriangle } from "lucide-react";
import {
  submitIncidentReport,
  generateReportId,
} from "../../services/incidentReportService";
import { FINDING_KIND, extractResolution } from "../../constants/findings";

const DEFAULT_VERIFICATION = [
  "Checkout processed successfully",
  "Webhook event received and logged",
  "Subscription activated",
  "Payment intent completed",
  "DNS resolving correctly",
  "SSL certificate valid",
  "Email sending successfully",
  "Site loading without errors",
];

const RESOLUTION_OPTIONS = [
  { value: "resolved", label: "Fully resolved" },
  { value: "partially_resolved", label: "Partially resolved" },
  { value: "unresolved", label: "Could not resolve" },
];

/**
 * Mandatory post-session report for the specialist. Rendered as a blocking
 * modal — the session cannot be completed until this is submitted.
 */
function IncidentReportForm({
  problem,
  specialistId,
  findings = [],
  events = [],
  onClose,
  onSubmitted,
}) {
  const reportId = useMemo(
    () => generateReportId(problem?.id || ""),
    [problem?.id]
  );

  /**
   * Seeded from the investigation record.
   *
   * The expert has already written the root cause and the actions they took as
   * findings; asking them to retype both is how you end up with a report that
   * disagrees with the timeline it is supposed to summarise. These are drafts,
   * not locks — the report is the customer-facing document and deserves editing.
   */
  const seeded = useMemo(() => {
    const { rootCause: rootCauseFinding } = extractResolution(findings);

    const actionLines = findings
      .filter(
        (finding) =>
          finding.kind === FINDING_KIND.ACTION || finding.kind === FINDING_KIND.FIX
      )
      .map((finding) => `- ${finding.text}`)
      .join("\n");

    return {
      rootCause: rootCauseFinding?.text || "",
      actionsTaken: actionLines,
    };
    // Seeded once on open; later edits to findings must not overwrite typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [rootCause, setRootCause] = useState(seeded.rootCause);
  const [actionsTaken, setActionsTaken] = useState(seeded.actionsTaken);
  const [checked, setChecked] = useState({});
  const [customSteps, setCustomSteps] = useState([]);
  const [customInput, setCustomInput] = useState("");
  const [resolutionStatus, setResolutionStatus] = useState("resolved");
  const [preventionNotes, setPreventionNotes] = useState("");
  const [sessionRecordingConsent, setSessionRecordingConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const selectedVerification = useMemo(
    () => [
      ...DEFAULT_VERIFICATION.filter((label) => checked[label]),
      ...customSteps,
    ],
    [checked, customSteps]
  );

  const canSubmit =
    rootCause.trim().length > 0 &&
    actionsTaken.trim().length > 0 &&
    selectedVerification.length > 0 &&
    !submitting;

  const toggleCheck = (label) =>
    setChecked((prev) => ({ ...prev, [label]: !prev[label] }));

  const addCustomStep = () => {
    const value = customInput.trim();
    if (!value) return;
    if (selectedVerification.includes(value)) {
      setCustomInput("");
      return;
    }
    setCustomSteps((prev) => [...prev, value]);
    setCustomInput("");
  };

  const removeCustomStep = (value) =>
    setCustomSteps((prev) => prev.filter((s) => s !== value));

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const result = await submitIncidentReport({
        problem,
        specialistId,
        events,
        form: {
          rootCause,
          actionsTaken,
          verificationSteps: selectedVerification,
          resolutionStatus,
          preventionNotes,
          sessionRecordingConsent,
        },
      });

      if (!result.success) {
        toast.error(result.error || "Could not submit the report.");
        setSubmitting(false);
        return;
      }

      toast.success("Incident Resolution Report submitted.");
      onSubmitted?.(result.reportId, resolutionStatus);
    } catch (err) {
      console.error("Incident report submit error:", err);
      toast.error("Failed to submit the report. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-modal flex items-start justify-center overflow-y-auto bg-slate-900 bg-opacity-60 p-3 sm:p-6">
      <div className="w-full max-w-2xl my-4 bg-white rounded-2xl border border-slate-200 shadow-2xl">
        {/* Header */}
        <div className="rounded-t-2xl bg-blue-600 px-5 sm:px-6 py-5 text-white relative">
          <button
            onClick={onClose}
            disabled={submitting}
            aria-label="Close report form"
            className="absolute top-4 right-4 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/15 hover:bg-white/25 transition disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" />
            <h2 className="text-lg sm:text-xl font-bold">
              Incident Resolution Report
            </h2>
          </div>
          <p className="mt-1 text-sm text-blue-100">
            Report ID:{" "}
            <span className="font-mono font-semibold text-white">
              {reportId}
            </span>
          </p>
          <p className="mt-1 text-xs text-blue-100">
            This formal report is required to close the session. It defines what
            was fixed and scopes the refund guarantee.
          </p>
        </div>

        {/* Body */}
        <div className="px-5 sm:px-6 py-5 space-y-6">
          {(seeded.rootCause || seeded.actionsTaken) && (
            <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
              Drafted from your findings on this incident. Edit anything below —
              this is the version the customer receives.
            </p>
          )}

          {/* Root cause */}
          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-1">
              Root cause identified <span className="text-rose-600">*</span>
            </label>
            <textarea
              value={rootCause}
              onChange={(e) => setRootCause(e.target.value)}
              placeholder="Describe exactly what was causing the issue..."
              className="w-full border border-slate-300 rounded-lg px-3 py-2.5 h-24 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition resize-none"
            />
          </div>

          {/* Actions taken */}
          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-1">
              Actions taken <span className="text-rose-600">*</span>
            </label>
            <textarea
              value={actionsTaken}
              onChange={(e) => setActionsTaken(e.target.value)}
              placeholder="List every change made, step by step..."
              className="w-full border border-slate-300 rounded-lg px-3 py-2.5 h-28 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition resize-none"
            />
          </div>

          {/* Verification */}
          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2">
              Verification completed{" "}
              <span className="text-rose-600">*</span>
              <span className="ml-1 text-xs font-normal text-slate-500">
                (select at least one)
              </span>
            </label>
            <div className="grid sm:grid-cols-2 gap-2">
              {DEFAULT_VERIFICATION.map((label) => (
                <label
                  key={label}
                  className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition ${
                    checked[label]
                      ? "bg-blue-50 border-blue-300 text-blue-900"
                      : "bg-white border-slate-200 text-slate-700 hover:border-blue-300"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(checked[label])}
                    onChange={() => toggleCheck(label)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>

            {/* Custom verification steps */}
            {customSteps.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {customSteps.map((step) => (
                  <span
                    key={step}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-600 text-white text-xs"
                  >
                    {step}
                    <button
                      type="button"
                      onClick={() => removeCustomStep(step)}
                      className="hover:text-blue-200"
                      aria-label={`Remove ${step}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="mt-2 flex gap-2">
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addCustomStep();
                  }
                }}
                placeholder="Add a custom verification step..."
                className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
              />
              <button
                type="button"
                onClick={addCustomStep}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 transition text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>
          </div>

          {/* Resolution status */}
          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2">
              Resolution status <span className="text-rose-600">*</span>
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
              {RESOLUTION_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition flex-1 ${
                    resolutionStatus === opt.value
                      ? "bg-blue-50 border-blue-300 text-blue-900 font-medium"
                      : "bg-white border-slate-200 text-slate-700 hover:border-blue-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="resolutionStatus"
                    value={opt.value}
                    checked={resolutionStatus === opt.value}
                    onChange={() => setResolutionStatus(opt.value)}
                    className="h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  {opt.label}
                </label>
              ))}
            </div>

            {resolutionStatus === "unresolved" && (
              <div className="mt-2 border-l-4 border-rose-500 bg-rose-50 rounded-lg px-3 py-2.5 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-rose-700">
                  This will trigger the refund eligibility review process.
                </p>
              </div>
            )}
          </div>

          {/* Prevention notes */}
          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-1">
              How to prevent this in future{" "}
              <span className="text-xs font-normal text-slate-500">
                (optional)
              </span>
            </label>
            <textarea
              value={preventionNotes}
              onChange={(e) => setPreventionNotes(e.target.value)}
              placeholder="Optional: recommend steps to prevent recurrence..."
              className="w-full border border-slate-300 rounded-lg px-3 py-2.5 h-20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition resize-none"
            />
          </div>

          {/* Recording consent */}
          <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={sessionRecordingConsent}
              onChange={(e) => setSessionRecordingConsent(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <span>Customer consented to session recording being referenced</span>
          </label>
        </div>

        {/* Footer */}
        <div className="rounded-b-2xl border-t border-slate-100 px-5 sm:px-6 py-4 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2.5 rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-100 transition text-sm font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Submitting…
              </>
            ) : (
              "Submit Report & Complete Session"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default IncidentReportForm;
